// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {MPProperties} from 'common/constants';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {MPContext} from 'common/mp-context';
import {Section} from 'common/components/items-menu/types';
import {SerializedCohort} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {propertiesUtils} from 'common/report/util/properties';
import {isSameProperty} from 'common/query-builder/query-block/properties-menu/util';
import {DISABLED_DUE_TO_CLASSIFIED_DATA, ResourceType} from 'common/report/constants';
import {PropertyType} from 'common/report/constants';
import {FilterBarTableColumn} from 'common/components/filter-bar-table/types';
import {IronContextRegistry} from 'common/report/context-registry';
import {getProfilePath} from 'common/report/util/profile/utils';
import {navigate} from 'common/util/url';
import {
  ColumnMovedEvent,
  ResizedColumnWidths,
  RowClickEvent,
  SelectionChangeEvent,
  SortChangeEvent,
  TableRow,
} from 'common/components/table/types';
import {shouldOpenInNewTab} from 'common/util/dom';
import {formatProperty} from 'common/util/format-property';

import {asPropertyItem} from './util';
import {DataCellRenderer, UserCellRenderer} from './mixins';
import {template} from './template';
import tableStyle from './table.inline.styl';
import tableCompactStyle from './compact.inline.styl';

interface UsersTableState {
  cohort: SerializedCohort;
  rows: TableRow[];
}
interface UsersTableJsxProps {
  attrs?: {
    'allow-x-overflow'?: boolean;
    'data-group-id': string;
    'emit-row-click'?: boolean;
    loading: boolean;
    'default-sort-field': any;
    'empty-message'?: string;
    'is-select-enabled': boolean;
    compact?: boolean;
    'has-resizable-columns'?: boolean;
    'showing-properties': PropertySectionItem[];
    'sensitive-properties': string[];
    'default-sort-order': number;
    'resized-column-widths'?: ResizedColumnWidths;
  };
  props?: {
    rows?: TableRow[];
  };
  on?: {
    selectionChange?: (ev: SelectionChangeEvent) => void;
    columnMoved?: (ev: ColumnMovedEvent) => void;
    sortChanged?: (ev: SortChangeEvent<TableRow>) => void;
  };
}

export class UsersTable extends Component<UsersTableState, UsersTableJsxProps['attrs'], any, any, IronContextRegistry> {
  private declare mpContext: MPContext;
  public declare getFilterSortedMenuSections: (sections: Section[]) => Section[];
  static get attrsSchema(): AttrsSchema<UsersTableJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      'allow-x-overflow': `boolean`,
      'data-group-id': `string`,
      loading: `boolean`,
      compact: `boolean`,
      'emit-row-click': `boolean`,
      'empty-message': `string`,
      'has-resizable-columns': `boolean`,
      'default-sort-field': `json`,
      'is-select-enabled': `boolean`,
      'showing-properties': `json`,
      'sensitive-properties': `json`,
      'default-sort-order': `number`,
      'resized-column-widths': {type: `json`, default: {}},
    };
  }

  get config() {
    return {
      template,
      contexts: [`currentProject` as const, `i18n` as const],
      defaultState: {
        cohort: null,
        colWidths: {},
        rows: [],
      },
      helpers: {
        getUserId: () => this.mpContext.userId,
        getDefaultSortField: () => {
          if (!this.attr(`default-sort-field`)) {
            return null;
          }
          return this.attr(`default-sort-field`).value;
        },
        getTableAttrs: () => {
          const attrs = {
            'allow-x-overflow': this.attr(`allow-x-overflow`),
            loading: this.attr(`loading`),
            'track-name': `users`,
            'multi-select': this.attr(`is-select-enabled`),
            'style-override': this.attr(`compact`) ? tableCompactStyle : tableStyle,
            'hide-titles': this.attr(`compact`),
            'search-filter': ``,
            'default-sort-field': this.helpers.getDefaultSortField(),
            'default-sort-order': this.attr(`default-sort-order`),
            'disable-auto-sorting': true,
            'draggable-columns': true,
            'resized-column-widths': this.attr(`resized-column-widths`),
          };

          if (this.attr(`empty-message`)) {
            attrs[`empty-message`] = this.attr(`empty-message`);
          }

          return attrs;
        },
        getTableColumns: () => {
          return this.attr(`showing-properties`).map((property) => {
            const title = this.formatPropertyName(property.value);
            const field = property.value;
            const col: FilterBarTableColumn = {
              field,
              title,
              titleMaxLength: 20,
              isResizable: this.attr(`has-resizable-columns`),
              isSortable: true,
            };
            if (property.scdEvent) {
              col.helpText = `On this page, Mixpanel will display the latest value for this history property.`;
            }
            const tooltip = this.isSensitiveProperty(property) ? DISABLED_DUE_TO_CLASSIFIED_DATA : null;
            if (isSameProperty(property, asPropertyItem(`$name`, PropertyType.String))) {
              col.cellRenderer = (row) => {
                const distinctId = row[MPProperties.DistinctId];
                const name = row[MPProperties.Name];
                const email = row[MPProperties.Email];
                const avatar = row[MPProperties.Avatar];
                return UserCellRenderer({
                  href: getProfilePath(distinctId, this.attr(`data-group-id`)),
                  distinctId,
                  email,
                  avatar,
                  emitRowClick: this.attr(`emit-row-click`),
                  tooltip: tooltip || name,
                  name,
                });
              };
            } else {
              col.cellRenderer = (row) => {
                const rowValue = formatProperty(field, row[field], {
                  resourceType: ResourceType.User,
                  timezone: this.getContext(`currentProject`).get().value?.timezone,
                  relativeTime: true,
                  dataGroupId: this.attr(`data-group-id`),
                  i18n: this.getContext(`i18n`),
                }).renderValue;
                return DataCellRenderer({
                  rowValue,
                  tooltip: tooltip || `${rowValue.raw}`,
                  allowLinks: !this.attr(`compact`),
                });
              };
            }
            return col;
          });
        },
        handleTableSelectionChanged: ({detail}: SelectionChangeEvent) => {
          const {changed, isToggleAll} = detail;
          const changedRows = Object.assign(
            {},
            ...changed.map((item) => ({
              [item.row.key]: Object.assign(item.row, {isSelected: item.isSelected}),
            })),
          );
          this.update({rows: this.state.rows.map((row) => changedRows[row.key] || row)});
          this.dispatchEvent(
            new CustomEvent(`selectionChanged`, {
              detail: {
                changed: changedRows,
                selected: this.state.rows.filter((row) => row.isSelected),
                isToggleAll,
              },
              bubbles: true,
            }),
          );
        },
        handleTableRowClicked: (ev: RowClickEvent) => {
          const {row} = ev.detail;
          if (this.attr(`emit-row-click`)) {
            this.dispatchEvent(
              new CustomEvent(`userSelected`, {
                detail: {
                  distinctID: row[MPProperties.DistinctId],
                },
                composed: true,
              }),
            );
            return;
          }
          const path = getProfilePath(row[MPProperties.DistinctId], this.attr(`data-group-id`));
          if (ev.detail.target.closest && ev.detail.target.closest(`a`)) {
            return;
          }
          if (shouldOpenInNewTab(ev)) {
            window.open(path, `_blank`);
          } else {
            navigate(path);
          }
        },
        handleTableSortChanged: ({detail}: SortChangeEvent<TableRow>) => {
          if (detail.isHeaderClick) {
            this.dispatchEvent(new CustomEvent(`sortChanged`, {detail, bubbles: true}));
          }
        },
        handleColumnMoved: ({detail}: ColumnMovedEvent) => {
          this.dispatchEvent(new CustomEvent(`columnMoved`, {detail, bubbles: true}));
        },
      },
    };
  }

  public connectedCallback() {
    this.mpContext = new MPContext();
    super.connectedCallback();
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
  }

  // separate function in order to stub it for testing
  formatPropertyName(name: string) {
    return propertiesUtils.renameProperty(name, ResourceType.User, this.getContext(`i18n`), {
      dataGroupId: this.attr(`data-group-id`),
    });
  }

  isSensitiveProperty(property: PropertySectionItem) {
    return (
      !(`customPropertyId` in property && property.customPropertyId) &&
      this.attr(`sensitive-properties`).includes(property.value)
    );
  }

  set rows(rows) {
    this.update({rows});
  }
}

export const UsersTableEl = defineAndMakeJsxFactory<UsersTableJsxProps>(`mp-users-table`, UsersTable);
export const UsersTableReact = panel2react<UsersTableJsxProps>(`mp-users-table`, UsersTable);
